<template>
  <div class="px-3 pt-2 h-full overflow-auto pb-5">
    <VariableSelect v-model="aiPayload.response_variable" :filterDataType="['Text']" label="Assign Response to Variable" @update:modelValue="emitPayload" />
    <VariableSelect v-model="aiPayload.reasoning_variable" :filterDataType="['Text']" label="Assign Reasoning to Variable (Optional)" class="mt-3" @update:modelValue="emitPayload" />
    <VariableSelect v-model="aiPayload.citations_variable" :filterDataType="['DataTable']" label="Assign Citation Urls to Variable (Optional)" class="mt-3" @update:modelValue="emitPayload" />
    <FormSelect
      :label="$t('evaluate.provider')"
      v-model="aiPayload.provider"
      :options="providers"
      :selectable="(option) => !option.disabled"
      :reduce="(option) => option.value"
      class="w-full mt-3"
      @update:modelValue="onProviderChange"
    />
    <FormSelect :label="$t('evaluate.model')" v-model="aiPayload.model" :options="models" :reduce="(option) => option.value" class="w-full mt-3" @update:modelValue="emitPayload" />
    <FormSelect
      :label="`${$t('evaluate.persona')} (Optional)`"
      v-model="aiPayload.persona"
      :options="personas"
      :reduce="(option) => option.value"
      class="w-full mt-3"
      show-add-button
      clearable
      @add="onAddPersonaClick"
      @update:modelValue="emitPayload"
    />
    <FormSelect
      :label="$t('evaluate.search_effort')"
      v-model="aiPayload.search_effort"
      :options="[
        { label: 'Low', value: 'low' },
        { label: 'Medium', value: 'medium' },
        { label: 'High', value: 'high' },
      ]"
      :reduce="(option) => option.value"
      class="w-full mt-3"
      @update:modelValue="emitPayload"
    />
    <PhraseEditor v-model="aiPayload.context" multiline :items="variables" class="w-full mt-3" :label="$t('evaluate.context')" @update:modelValue="emitPayload" />
    <FormSelect
      :label="$t('evaluate.replace_citations')"
      v-model="aiPayload.replace_citations"
      :options="[
        { label: 'Yes', value: 'yes' },
        { label: 'No', value: 'no' },
      ]"
      :reduce="(option) => option.value"
      class="w-full mt-3"
      @update:modelValue="emitPayload"
    />
    <FormInput :label="$t('evaluate.max_tokens')" v-model="aiPayload.max_length" type="number" class="w-full mt-3" @update:modelValue="emitPayload" />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import CreatePersonaModal from '@/components/project/CreatePersonaModal.vue';
import EvaluateItemsConfig from '@/components/flow/evaluate/EvaluateItemsConfig';
import useAIModelSource from '@/components/flow/evaluate/useAIModelSource';
import { AI_FEATURES } from '@/constants';
import debounce from '../../../helpers/debounce';

export default {
  name: 'AIDeepResearchConfig',
  mixins: [useAIModelSource],
  props: {
    item: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      aiPayload: structuredClone(EvaluateItemsConfig['ai-deepresearch'].payload),
    };
  },
  computed: {
    ...mapState(['availableAIProviders']),
    ...mapGetters(['getProjectAIProviderOptions', 'getProjectAIProviders', 'getProjectPersonas', 'getProjectVariables']),
    payload() {
      return {
        id: this.item.id,
        type: this.item.type,
        payload: this.aiPayload,
      };
    },
    variables() {
      return Object.keys(this.getProjectVariables()).map((i) => ({ label: i, value: i }));
    },
    providers() {
      return this.getProjectAIProviderOptions();
    },
    models() {
      if (!this.aiPayload.provider) return [];
      return this.modelsForProvider(this.aiPayload.provider)
        .filter((model) => (model.features || []).includes(AI_FEATURES.DEEP_RESEARCH))
        .map((item) => ({ label: `<b>${item.name}</b> - <i class="text-11">Cost: ${item.cost}</i>`, value: item.model }));
    },
    projectId() {
      return this.$route.params.projectId;
    },
    personas() {
      return Object.values(this.getProjectPersonas()).map((item) => ({ label: item.name, value: item.persona_id }));
    },
  },
  methods: {
    // eslint-disable-next-line
    emitPayload: debounce(function () {
      this.aiPayload.max_length = this.aiPayload.max_length.toString();
      this.$emit('update', this.payload);
    }, 300),
    onProviderChange() {
      this.aiPayload.model = '';
    },
    onAddPersonaClick() {
      this.$showModal(CreatePersonaModal, { projectId: this.projectId });
    },
  },
  watch: {
    item: {
      handler() {
        if (this.item) {
          this.aiPayload = JSON.parse(JSON.stringify(this.item.payload));
        }
      },
      deep: true,
      immediate: true,
    },
  },
};
</script>
